/**
 * Maps backend polygon validation messages/codes to i18n keys for field import UI.
 */

export type PolygonValidationErrorCode =
  | 'self_intersection'
  | 'not_closed'
  | 'area_too_small'
  | 'area_too_large'
  | 'dimension_too_small'
  | 'dimension_too_large'
  | 'empty'
  | 'invalid'
  | 'unknown';

export interface MappedPolygonImportError {
  code: PolygonValidationErrorCode;
  /** ngx-translate key shown on the plaque */
  messageKey: string;
  /** Original backend message (kept for tooltip / fallback) */
  rawMessage: string;
}

const CODE_TO_MESSAGE_KEY: Record<PolygonValidationErrorCode, string> = {
  self_intersection: 'IMPORT_POLYGON_SELF_INTERSECTION',
  not_closed: 'IMPORT_POLYGON_NOT_CLOSED',
  area_too_small: 'IMPORT_POLYGON_TOO_SMALL',
  area_too_large: 'IMPORT_POLYGON_TOO_LARGE',
  dimension_too_small: 'IMPORT_POLYGON_DIMENSION_TOO_SMALL',
  dimension_too_large: 'IMPORT_POLYGON_DIMENSION_TOO_LARGE',
  empty: 'IMPORT_POLYGON_EMPTY',
  invalid: 'IMPORT_POLYGON_INVALID',
  unknown: 'IMPORT_POLYGON_INVALID'
};

function detectCode(raw: string): PolygonValidationErrorCode {
  const message = (raw || '').toLowerCase();

  if (
    message.includes('polygon_self_intersection') ||
    message.includes('self-intersect') ||
    message.includes('self intersection')
  ) {
    return 'self_intersection';
  }

  if (
    message.includes('polygon_not_closed') ||
    message.includes('not closed') ||
    message.includes('must be closed')
  ) {
    return 'not_closed';
  }

  if (
    message.includes('polygon_area_too_small') ||
    message.includes('less than the minimum allowed')
  ) {
    return 'area_too_small';
  }

  if (
    message.includes('polygon_area_too_large') ||
    message.includes('exceeds the maximum allowed')
  ) {
    return 'area_too_large';
  }

  if (
    message.includes('polygon_dimension_too_small') ||
    message.includes('smallest bounding-box side')
  ) {
    return 'dimension_too_small';
  }

  if (
    message.includes('polygon_dimension_too_large') ||
    message.includes('largest bounding-box side')
  ) {
    return 'dimension_too_large';
  }

  if (
    message.includes('polygon_empty') ||
    message.includes('empty or missing')
  ) {
    return 'empty';
  }

  if (
    message.includes('polygon_invalid') ||
    message.includes('polygon_area_invalid') ||
    message.includes('polygon_dimension_invalid')
  ) {
    return 'invalid';
  }

  return 'unknown';
}

export function mapPolygonImportError(rawMessage: string): MappedPolygonImportError {
  const code = detectCode(rawMessage);
  return {
    code,
    messageKey: CODE_TO_MESSAGE_KEY[code],
    rawMessage: rawMessage || ''
  };
}

/** Unique plaques by semantic code (avoid duplicate banners for same error type). */
export function mapPolygonImportErrors(rawMessages: string[] | null | undefined): MappedPolygonImportError[] {
  if (!rawMessages?.length) {
    return [];
  }

  const byCode = new Map<PolygonValidationErrorCode, MappedPolygonImportError>();

  for (const raw of rawMessages) {
    if (!raw || !String(raw).trim()) {
      continue;
    }

    const mapped = mapPolygonImportError(String(raw));
    if (!byCode.has(mapped.code)) {
      byCode.set(mapped.code, mapped);
    }
  }

  return Array.from(byCode.values());
}

/** Extracts error strings from HTTP 400 body or PlantingsImportResult. */
export function extractImportErrorMessages(errorOrResult: any): string[] {
  if (!errorOrResult) {
    return [];
  }

  // PlantingsImportResult (200 with soft errors) or typed body
  if (Array.isArray(errorOrResult.errors) && errorOrResult.errors.length > 0) {
    return errorOrResult.errors.map((e: unknown) => String(e));
  }

  const body = errorOrResult.error ?? errorOrResult;

  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    return body.errors.map((e: unknown) => String(e));
  }

  if (typeof body?.message === 'string' && body.message.trim()) {
    return [body.message];
  }

  if (typeof errorOrResult.message === 'string' && errorOrResult.message.trim()) {
    // Angular HttpErrorResponse.message is often the generic status text — skip it.
    if (!errorOrResult.message.startsWith('Http failure')) {
      return [errorOrResult.message];
    }
  }

  return [];
}
